<template>
  <span v-if="!isListed" class="authority-purchase__price smallText">
    Not listed
  </span>
  <UnitButton
    v-else-if="canCheckout"
    class="authority-purchase__trigger"
    variant="secondary"
    size="micro"
    :aria-label="`Buy ${item.title || item.itemName} for ${listingPrice}`"
    @click.stop="openCheckout"
  >
    {{ listingPrice }}
  </UnitButton>
  <span v-else class="authority-purchase__price smallText">
    {{ listingPrice }}
  </span>

  <ConfirmDialog
    :visible="dialogVisible"
    :title="dialogTitle"
    :tone="phase === 'success' ? 'success' : 'neutral'"
    :show-cancel="phase !== 'success'"
    :confirm-label="confirmLabel"
    :cancel-label="'Cancel'"
    :busy="isBusy"
    :confirm-disabled="confirmDisabled"
    @confirm="confirmCheckout"
    @close="closeCheckout"
  >
    <template #body>
      <div class="authority-purchase" data-authority-purchase-checkout>
        <p class="authority-purchase__seller-price">
          Seller price: <strong>{{ sellerPrice }}</strong>
        </p>

        <LoadingState
          v-if="phase === 'quoting'"
          variant="section"
          message="Checking eligible organisation Wallets…"
        />
        <p
          v-else-if="phase === 'settling'"
          class="authority-purchase__status"
          role="status"
          aria-live="polite"
        >
          Completing the purchase…
        </p>

        <fieldset
          v-if="payerCandidates.length > 1 && phase !== 'success'"
          class="authority-purchase__payers"
          :disabled="isBusy"
        >
          <legend>Pay from</legend>
          <label
            v-for="candidate in payerCandidates"
            :key="candidate.orgId"
            class="authority-purchase__payer"
          >
            <input
              type="radio"
              :name="payerGroupName"
              :value="candidate.orgId"
              :checked="candidate.orgId === selectedPayerOrgId"
              @change="choosePayer(candidate.orgId)"
            />
            <span>
              <strong>{{ candidate.name }}</strong>
              <span class="smallText">{{ candidate.currency }}</span>
            </span>
            <span
              v-if="candidate.isActiveOrganisation"
              class="authority-purchase__acting smallText"
            >
              Acting organisation
            </span>
          </label>
        </fieldset>

        <dl
          v-if="quote && phase !== 'success'"
          class="authority-purchase__summary"
        >
          <div>
            <dt>Pay</dt>
            <dd>{{ payerDebit }}</dd>
          </div>
          <div>
            <dt>From</dt>
            <dd>{{ quote.payer.name }}</dd>
          </div>
          <div>
            <dt>Seller receives</dt>
            <dd>{{ quotedSellerPrice }}</dd>
          </div>
        </dl>

        <div v-if="phase === 'success'" class="authority-purchase__success">
          <p role="status">Purchase complete.</p>
          <p v-if="settlementResult" class="smallText">
            Paid {{ settledDebit }} from {{ quote?.payer.name }}.
          </p>
        </div>

        <p
          v-if="noticeMessage && phase !== 'success'"
          class="authority-purchase__notice smallText"
          role="status"
          aria-live="polite"
        >
          {{ noticeMessage }}
        </p>
        <p
          v-if="errorMessage && phase !== 'success'"
          class="authority-purchase__error smallText"
          role="alert"
        >
          {{ errorMessage }}
        </p>
      </div>
    </template>
  </ConfirmDialog>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";

import ConfirmDialog from "@/components/GenericComponents/ConfirmDialog.vue";
import LoadingState from "@/components/GenericComponents/LoadingState.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import {
  isMonetaryAuthorityQuotesFeatureEnabled,
  isMonetaryAuthoritySettlementFeatureEnabled,
} from "@/config/runtimeConfig";
import { deriveItemPlacementReference } from "@/services/itemPlacementReference";
import {
  createMonetaryAuthorityOperationId,
  monetaryAuthorityService,
} from "@/services/monetaryAuthorityService";
import { formatAtomicCurrency } from "@/utils/atomicCurrencyFormatting";

let purchaseInstanceCount = 0;

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  actorUid: {
    type: String,
    default: null,
  },
  quotesEnabled: {
    type: Boolean,
    default: isMonetaryAuthorityQuotesFeatureEnabled,
  },
  settlementEnabled: {
    type: Boolean,
    default: isMonetaryAuthoritySettlementFeatureEnabled,
  },
});

const emit = defineEmits(["purchased"]);
const payerGroupName = `authority-payer-${++purchaseInstanceCount}`;
const dialogVisible = ref(false);
const phase = ref("idle");
const quote = ref(null);
const settlementResult = ref(null);
const payerCandidates = ref([]);
const selectedPayerOrgId = ref(null);
const errorMessage = ref("");
const noticeMessage = ref("");
const needsQuoteRefresh = ref(false);

let contextGeneration = 0;
let quoteRequestGeneration = 0;
let quoteAttempt = null;
let settlementAttempt = null;

const isListed = computed(
  () =>
    props.item?._authorityPresentation === true &&
    props.item?.listing !== null &&
    typeof props.item?.priceAtomic === "string" &&
    props.item.priceAtomic !== "0"
);
const canCheckout = computed(
  () =>
    isListed.value &&
    props.quotesEnabled &&
    props.settlementEnabled &&
    typeof props.actorUid === "string" &&
    props.actorUid.length > 0
);
const isBusy = computed(
  () => phase.value === "quoting" || phase.value === "settling"
);
const contextSignature = computed(() =>
  [
    props.actorUid || "",
    props.item?.itemId || "",
    props.item?.organisation || "",
    props.item?.priceAtomic || "",
  ].join("\u0000")
);

function formatAmount(amountAtomic, currency) {
  try {
    return formatAtomicCurrency({
      amountAtomic,
      amountScale: 9,
      currencySymbol: currency,
    });
  } catch (_error) {
    return "Price unavailable";
  }
}

const listingPrice = computed(() =>
  isListed.value
    ? formatAmount(
        props.item.priceAtomic,
        props.item.currencySymbol || props.item.currencyCode
      )
    : "Not listed"
);
const sellerPrice = computed(() =>
  quote.value
    ? formatAmount(
        quote.value.listing.unitPriceAtomic,
        quote.value.payee.currency
      )
    : listingPrice.value
);
const quotedSellerPrice = computed(() =>
  quote.value
    ? formatAmount(quote.value.amounts.creditAtomic, quote.value.payee.currency)
    : ""
);
const payerDebit = computed(() =>
  quote.value
    ? formatAmount(quote.value.amounts.debitAtomic, quote.value.payer.currency)
    : ""
);
const settledDebit = computed(() =>
  settlementResult.value && quote.value
    ? formatAmount(
        settlementResult.value.debitAtomic,
        quote.value.payer.currency
      )
    : ""
);
const dialogTitle = computed(() =>
  phase.value === "success"
    ? "Purchase complete"
    : `Buy ${props.item?.title || props.item?.itemName || "item"}?`
);
const confirmLabel = computed(() => {
  if (phase.value === "success") return "Done";
  if (phase.value === "quoting") return "Checking…";
  if (phase.value === "settling") return "Buying…";
  if (needsQuoteRefresh.value) return "Refresh quote";
  if (quote.value && errorMessage.value) return "Retry purchase";
  if (quote.value) return "Buy";
  return errorMessage.value ? "Retry" : "Continue";
});
const confirmDisabled = computed(
  () =>
    !canCheckout.value ||
    isBusy.value ||
    (phase.value !== "success" && !quote.value && !errorMessage.value)
);

function resetSession() {
  contextGeneration += 1;
  quoteRequestGeneration += 1;
  dialogVisible.value = false;
  phase.value = "idle";
  quote.value = null;
  settlementResult.value = null;
  payerCandidates.value = [];
  selectedPayerOrgId.value = null;
  errorMessage.value = "";
  noticeMessage.value = "";
  needsQuoteRefresh.value = false;
  quoteAttempt = null;
  settlementAttempt = null;
}

function contextIsCurrent(capture) {
  return (
    capture.contextGeneration === contextGeneration &&
    capture.requestGeneration === quoteRequestGeneration &&
    capture.signature === contextSignature.value &&
    capture.actorUid === props.actorUid
  );
}

function readableError(error) {
  return typeof error?.message === "string" && error.message.length > 0
    ? error.message
    : "This purchase is temporarily unavailable.";
}

async function requestQuote(payerOrgId = null, { forceNew = false } = {}) {
  if (!canCheckout.value) return;
  const signature = contextSignature.value;
  const actorUid = props.actorUid;
  const context = contextGeneration;
  const requestGeneration = ++quoteRequestGeneration;
  const normalizedPayerOrgId = payerOrgId || null;
  selectedPayerOrgId.value = normalizedPayerOrgId;
  quote.value = null;
  settlementResult.value = null;
  settlementAttempt = null;
  phase.value = "quoting";
  errorMessage.value = "";
  noticeMessage.value = "";
  needsQuoteRefresh.value = false;

  const capture = {
    contextGeneration: context,
    requestGeneration,
    signature,
    actorUid,
  };

  try {
    const listingId = await deriveItemPlacementReference({
      itemId: props.item.itemId,
      orgId: props.item.organisation,
    });
    if (!contextIsCurrent(capture)) return;

    if (
      forceNew ||
      !quoteAttempt ||
      quoteAttempt.signature !== signature ||
      quoteAttempt.payerOrgId !== normalizedPayerOrgId
    ) {
      quoteAttempt = Object.freeze({
        signature,
        payerOrgId: normalizedPayerOrgId,
        operationId: createMonetaryAuthorityOperationId(),
      });
    }

    const result = await monetaryAuthorityService.quoteListingPurchase({
      operationId: quoteAttempt.operationId,
      listingId,
      quantity: 1,
      payerOrgId: normalizedPayerOrgId,
    });
    if (!contextIsCurrent(capture)) return;
    if (result.expiresAt <= Date.now()) {
      quoteAttempt = null;
      phase.value = "idle";
      errorMessage.value =
        "This quote expired. Request a new quote to continue.";
      return;
    }

    quote.value = result;
    selectedPayerOrgId.value = result.payer.orgId;
    payerCandidates.value = [...result.payerSelection.candidates];
    phase.value = "ready";
  } catch (error) {
    if (!contextIsCurrent(capture)) return;
    phase.value = "idle";
    errorMessage.value = readableError(error);
  }
}

function openCheckout() {
  if (!canCheckout.value) return;
  dialogVisible.value = true;
  if (quote.value && quote.value.expiresAt > Date.now()) {
    phase.value = "ready";
    return;
  }
  requestQuote(selectedPayerOrgId.value, {
    forceNew: Boolean(quote.value || !quoteAttempt),
  });
}

function closeCheckout() {
  if (isBusy.value) return;
  if (phase.value === "success") {
    resetSession();
    return;
  }
  dialogVisible.value = false;
}

function choosePayer(orgId) {
  if (isBusy.value || orgId === selectedPayerOrgId.value) return;
  requestQuote(orgId, { forceNew: true });
}

async function settleQuote() {
  if (!quote.value || isBusy.value || !canCheckout.value) return;
  if (quote.value.expiresAt <= Date.now()) {
    noticeMessage.value =
      "The quote expired. Review the refreshed amount before buying.";
    await requestQuote(selectedPayerOrgId.value, { forceNew: true });
    return;
  }

  const currentQuote = quote.value;
  const capture = {
    contextGeneration,
    requestGeneration: quoteRequestGeneration,
    signature: contextSignature.value,
    actorUid: props.actorUid,
  };
  try {
    if (
      !settlementAttempt ||
      settlementAttempt.quoteId !== currentQuote.quoteId
    ) {
      settlementAttempt = Object.freeze({
        quoteId: currentQuote.quoteId,
        operationId: createMonetaryAuthorityOperationId(),
      });
    }
  } catch (error) {
    phase.value = "ready";
    errorMessage.value = readableError(error);
    return;
  }
  phase.value = "settling";
  errorMessage.value = "";
  noticeMessage.value = "";
  needsQuoteRefresh.value = false;

  try {
    const result = await monetaryAuthorityService.settleUnitQuote({
      operationId: settlementAttempt.operationId,
      quoteId: settlementAttempt.quoteId,
    });
    if (!contextIsCurrent(capture)) return;
    if (
      result.kind !== currentQuote.kind ||
      result.payerOrgId !== currentQuote.payer.orgId ||
      result.payeeOrgId !== currentQuote.payee.orgId ||
      result.debitAtomic !== currentQuote.amounts.debitAtomic ||
      result.creditAtomic !== currentQuote.amounts.creditAtomic
    ) {
      const invalidResponse = new Error("The payment response was invalid.");
      invalidResponse.code = "invalid-response";
      throw invalidResponse;
    }
    settlementResult.value = result;
    phase.value = "success";
    emit("purchased", result);
  } catch (error) {
    if (!contextIsCurrent(capture)) return;
    phase.value = "ready";
    errorMessage.value = readableError(error);
    needsQuoteRefresh.value = [
      "aborted",
      "failed-precondition",
      "invalid-response",
      "not-found",
    ].includes(error?.code);
  }
}

function confirmCheckout() {
  if (phase.value === "success") {
    closeCheckout();
    return;
  }
  if (needsQuoteRefresh.value) {
    requestQuote(selectedPayerOrgId.value, { forceNew: true });
    return;
  }
  if (quote.value) {
    settleQuote();
    return;
  }
  requestQuote(selectedPayerOrgId.value);
}

watch(contextSignature, resetSession);
watch(canCheckout, (enabled) => {
  if (!enabled) resetSession();
});
onBeforeUnmount(() => {
  contextGeneration += 1;
  quoteRequestGeneration += 1;
});
</script>

<style scoped>
.authority-purchase__price {
  color: var(--color-text-primary);
  font-variant-numeric: tabular-nums;
}

.authority-purchase__trigger {
  font-variant-numeric: tabular-nums;
}

.authority-purchase {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--space-4);
}

.authority-purchase p {
  margin: 0;
}

.authority-purchase__seller-price {
  overflow-wrap: anywhere;
}

.authority-purchase__status,
.authority-purchase__success {
  color: var(--color-text-secondary);
}

.authority-purchase__payers {
  display: grid;
  min-width: 0;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  border: 0;
}

.authority-purchase__payers legend {
  margin-block-end: var(--space-2);
  font-weight: var(--font-weight-medium);
}

.authority-purchase__payer {
  display: grid;
  min-width: 0;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
  background: var(--color-surface-subtle);
  cursor: pointer;
}

.authority-purchase__payer:has(input:focus-visible) {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
}

.authority-purchase__payer > span:not(.authority-purchase__acting) {
  display: flex;
  min-width: 0;
  flex-direction: column;
  overflow-wrap: anywhere;
}

.authority-purchase__acting {
  color: var(--color-text-secondary);
  text-align: end;
}

.authority-purchase__summary {
  display: grid;
  gap: var(--space-2);
  margin: 0;
}

.authority-purchase__summary > div {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-4);
}

.authority-purchase__summary dt {
  color: var(--color-text-secondary);
}

.authority-purchase__summary dd {
  margin: 0;
  overflow-wrap: anywhere;
  font-variant-numeric: tabular-nums;
  text-align: end;
}

.authority-purchase__notice {
  color: var(--color-text-secondary);
}

.authority-purchase__error {
  color: var(--color-feedback-danger-foreground);
}

</style>
